import React, { useState, useRef, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { submitLead } from "../utils/api";

const countryCodes = [
  { code: "+1", country: "US" },
  { code: "+44", country: "UK" },
  { code: "+974", country: "QA" },
  { code: "+971", country: "AE" },
  { code: "+966", country: "SA" },
  { code: "+91", country: "IN" },
  { code: "+92", country: "PK" },
  { code: "+61", country: "AU" },
  { code: "+49", country: "DE" },
  { code: "+33", country: "FR" },
  { code: "+86", country: "CN" },
  { code: "+81", country: "JP" },
  { code: "+82", country: "KR" },
  { code: "+55", country: "BR" },
  { code: "+234", country: "NG" },
  { code: "+27", country: "ZA" },
  { code: "+65", country: "SG" },
  { code: "+60", country: "MY" },
  { code: "+62", country: "ID" },
  { code: "+20", country: "EG" },
];

function CountryCodeSelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = countryCodes.find((c) => c.code === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white/5 border border-purple-900/30 rounded-lg px-2 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500 w-[85px] flex items-center justify-between"
        data-testid="popup-country-code"
      >
        <span>{selected?.code} {selected?.country}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-[130px] bg-[#1a1a2e] border border-purple-900/30 rounded-lg shadow-xl max-h-[180px] overflow-y-auto z-50">
          {countryCodes.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange(c.code); setIsOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-600/20 transition-colors ${
                c.code === value ? "text-purple-400 bg-purple-900/20" : "text-gray-300"
              }`}
            >
              {c.code} {c.country}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InquiryPopup({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ name: "", email: "", countryCode: "+974", phone: "", description: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, phone: value });
    if (errors.phone) setErrors({ ...errors, phone: "" });
  };

  const handleEmailChange = (e) => {
    setFormData({ ...formData, email: e.target.value });
    if (errors.email) setErrors({ ...errors, email: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email (e.g., user@example.com)";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!validatePhone(formData.phone)) newErrors.phone = "Please enter exactly 10 digits after country code";
    if (!formData.description.trim()) newErrors.description = "Description is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitted(true);
    try {
      await submitLead({ name: formData.name, email: formData.email, phone: formData.phone, countryCode: formData.countryCode, message: formData.description, source: "popup" });
    } catch (err) { /* saved locally even if API fails */ }
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: "", email: "", countryCode: "+974", phone: "", description: "" });
      onClose();
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4" data-testid="inquiry-popup">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative bg-[#0d0d1a] border-2 border-purple-500/50 rounded-2xl w-full max-w-[800px] max-h-[85vh] overflow-y-auto flex flex-col md:flex-row shadow-2xl shadow-purple-900/30"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 transition-colors"
              data-testid="popup-close-btn"
            >
              <X size={20} />
            </button>

            {/* Left - Image (hidden on mobile) */}
            <div className="hidden md:block md:w-[40%] md:min-h-[500px] relative flex-shrink-0">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=700&fit=crop"
                alt="Office"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0d0d1a]/50" />
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-white font-semibold text-lg">Let's Build Together</h3>
                <p className="text-gray-300 text-xs mt-1">Share your idea and we'll get back to you within 24 hours.</p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="w-full md:w-[60%] p-5 sm:p-6 md:p-8">
              <h2 className="text-white font-semibold text-xl mb-1">Get in Touch</h2>
              <p className="text-gray-400 text-sm mb-5">Fill in the details and our team will reach out.</p>

              {submitted ? (
                <div className="flex items-center justify-center h-[280px]">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-purple-600/20 flex items-center justify-center mx-auto mb-3">
                      <span className="text-purple-400 text-2xl">✓</span>
                    </div>
                    <p className="text-white font-semibold">Submitted Successfully!</p>
                    <p className="text-gray-400 text-sm mt-1">We'll get back to you shortly.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="inquiry-form">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: "" }); }}
                      className={`w-full bg-white/5 border ${errors.name ? "border-red-500" : "border-purple-900/30"} rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500`}
                      data-testid="popup-name"
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleEmailChange}
                      className={`w-full bg-white/5 border ${errors.email ? "border-red-500" : "border-purple-900/30"} rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500`}
                      data-testid="popup-email"
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <CountryCodeSelect
                        value={formData.countryCode}
                        onChange={(code) => setFormData({ ...formData, countryCode: code })}
                      />
                      <input
                        type="tel"
                        placeholder="Phone (10 digits)"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className={`flex-1 min-w-0 bg-white/5 border ${errors.phone ? "border-red-500" : "border-purple-900/30"} rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500`}
                        data-testid="popup-phone"
                      />
                    </div>
                    {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <textarea
                      placeholder="Describe your project..."
                      rows={3}
                      value={formData.description}
                      onChange={(e) => { setFormData({ ...formData, description: e.target.value }); if (errors.description) setErrors({ ...errors, description: "" }); }}
                      className={`w-full bg-white/5 border ${errors.description ? "border-red-500" : "border-purple-900/30"} rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none`}
                      data-testid="popup-description"
                    />
                    {errors.description && <p className="text-red-400 text-xs mt-1">{errors.description}</p>}
                  </div>
                  <button
                    type="submit"
                    className="w-full cta-button text-white py-3 rounded-lg text-sm font-semibold"
                    data-testid="popup-submit"
                  >
                    Submit Inquiry
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
